/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$accordion-inner-spacing: 16px !default;
$accordion-inner-spacing-desktop: 16px !default;
$accordion-inner-spacing-tablet: 12px !default;
$accordion-inner-spacing-mobile: 24px !default;
$accordion-pane-border-width: 1px !default;
$accordion-pane-border-width-top: $accordion-pane-border-width !default;
$accordion-pane-border-width-right: $accordion-pane-border-width !default;
$accordion-pane-border-width-bottom: $accordion-pane-border-width !default;
$accordion-pane-border-width-left: $accordion-pane-border-width !default;
$accordion-pane-highlight-bar-width: 4px !default;
$accordion-hover-shadow: none !default;
$accordion-active-shadow: none !default;
$accordion-default-radius: 0 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  width: 100%;
  overflow: visible;
  max-width: 100%;
  @include eon-typography();
}

.#{$prefix}accordion-wrapper {
  position: relative;
  width: calc(100% + #{$accordion-inner-spacing} * 2);
  height: auto;
  margin-left: (-#{$accordion-inner-spacing});
  margin-right: (-#{$accordion-inner-spacing});
  overflow: visible;

  @include for-size(desktop) {
    width: calc(100% + #{$accordion-inner-spacing-desktop} * 2);
    margin-left: (-#{$accordion-inner-spacing-desktop});
    margin-right: (-#{$accordion-inner-spacing-desktop});
  }

  @include for-size(tablet) {
    width: calc(100% + #{$accordion-inner-spacing-tablet} * 2);
    margin-left: (-#{$accordion-inner-spacing-tablet});
    margin-right: (-#{$accordion-inner-spacing-tablet});
  }

  @include for-size(phone) {
    width: calc(100% + #{$accordion-inner-spacing-mobile} * 2);
    margin-left: (-#{$accordion-inner-spacing-mobile});
    margin-right: (-#{$accordion-inner-spacing-mobile});
  }

  * {
    box-sizing: border-box;
  }

  .#{$prefix}accordion {
    position: relative;
    border: $accordion-pane-border-width solid transparent;
    margin-top: -1px;
    border-radius: $accordion-default-radius;

    &:first-of-type {
      margin-top: var(--marginTop, 0);
    }

    &::after {
      content: "";
      position: absolute;
      left: #{$accordion-inner-spacing};
      right: #{$accordion-inner-spacing};
      bottom: -1px;
      height: $accordion-pane-border-width;
      background-color: v(borderHoverColor);

      @include for-size(desktop) {
        left: #{$accordion-inner-spacing-desktop};
        right: #{$accordion-inner-spacing-desktop};
      }

      @include for-size(tablet) {
        left: #{$accordion-inner-spacing-tablet};
        right: #{$accordion-inner-spacing-tablet};
      }

      @include for-size(phone) {
        left: #{$accordion-inner-spacing-mobile};
        right: #{$accordion-inner-spacing-mobile};
      }
    }

    .#{$prefix}accordion-inner {
      position: relative;
      border-radius: $accordion-default-radius;

      @if $accordion-default-radius != 0 {
        overflow: hidden;
      }
    }

    &.#{$prefix}view-with-bar {
      .#{$prefix}accordion-inner {
        &::before {
          content: unset;

          transition-property: all;
          transition-timing-function: $transition-timing-function;
          transition-delay: 0;
          transition-duration: 300ms;
        }
      }
    }

    &:hover,
    &.#{$prefix}force-hover-state {
      border-top: $accordion-pane-border-width-top solid v(borderHoverColor);
      border-right: $accordion-pane-border-width-right solid v(borderHoverColor);
      border-bottom: $accordion-pane-border-width-bottom solid v(borderHoverColor);
      border-left: $accordion-pane-border-width-left solid v(borderHoverColor);
      background-color: v(accordionHoverColor);
      outline: none;

      &.#{$prefix}view-with-bar {
        .#{$prefix}accordion-inner {
          &::before {
            content: "";
            position: absolute;
            left: -1px;
            top: -1px;
            width: $accordion-pane-highlight-bar-width;
            height: calc(100% + 2px);
            background-color: v(borderHoverColor);
            z-index: 10;
          }
        }
      }

      &:not(.#{$prefix}hide-shadow) {
        z-index: 1;
        box-shadow: $accordion-hover-shadow;

        &:after {
          bottom: -1px;
        }

        &:not(.#{$prefix}force-hover-state) {
          z-index: 40;
        }
      }
    }

    &.#{$prefix}active {
      border-top: $accordion-pane-border-width-top solid v(borderHoverColor);
      border-right: $accordion-pane-border-width-right solid v(borderHoverColor);
      border-bottom: $accordion-pane-border-width-bottom solid v(borderHoverColor);
      border-left: $accordion-pane-border-width-left solid v(borderHoverColor);
      background-color: v(activeAccordionBackgroundColor);

      &.#{$prefix}view-with-bar {
        .#{$prefix}accordion-inner {
          &::before {
            content: "";
            position: absolute;
            left: -1px;
            top: -1px;
            width: $accordion-pane-highlight-bar-width;
            height: calc(100% + 2px);
            background-color: v(activeAccordionBarColor);
            z-index: 1;
          }
        }
      }

      &:after {
        bottom: -1px;
      }

      &:not(.#{$prefix}hide-shadow) {
        box-shadow: $accordion-active-shadow;

        &:after {
          bottom: -1px;
        }
      }
    }
  }

  .#{$prefix}accordion-pane {
    position: relative;
    color: v(textColor);
    cursor: pointer;
    text-align: left;
    outline: none;

    .#{$prefix}paneTitle {
      padding-right: calc(36px + $accordion-inner-spacing);

      @include for-size(phone) {
        padding-right: calc(26px + $accordion-inner-spacing-mobile);
      }
    }

    svg,
    eon-ui-icon {
      position: absolute;
      right: #{$accordion-inner-spacing};
      padding-left: 8px;
      padding-right: 8px;
      top: 24px;
      transform: translateY(-50%);
      fill: v(textColor);

      transition-property: all;
      transition-timing-function: $transition-timing-function;
      transition-delay: 0;
      transition-duration: 300ms;

      @include for-size(desktop) {
        right: #{$accordion-inner-spacing-desktop};
      }

      @include for-size(tablet) {
        right: #{$accordion-inner-spacing-tablet};
      }

      @include for-size(phone) {
        right: #{$accordion-inner-spacing-mobile};
      }
    }

    &.#{$prefix}active {
      svg,
      eon-ui-icon {
        transform: translateY(-50%) rotate(-180deg);
        transform-origin: center center;
      }
    }

    @include for-size(desktop-or-larger) {
      &.#{$prefix}hidden {
        display: none;
      }
    }
  }

  &.#{$prefix}small {
    .#{$prefix}accordion-pane {
      font-size: 18px;
      line-height: 24px;
      font-weight: $font-weight-bold;
      padding-top: 12px;
      padding-bottom: 12px;
      padding-left: #{$accordion-inner-spacing};
      padding-right: #{$accordion-inner-spacing};

      @include for-size(desktop) {
        padding-left: #{$accordion-inner-spacing-desktop};
        padding-right: #{$accordion-inner-spacing-desktop};
      }

      @include for-size(tablet) {
        padding-left: #{$accordion-inner-spacing-tablet};
        padding-right: #{$accordion-inner-spacing-tablet};
      }

      @include for-size(phone) {
        padding-left: #{$accordion-inner-spacing-mobile};
        padding-right: #{$accordion-inner-spacing-mobile};
      }
    }
  }

  &.#{$prefix}large {
    .#{$prefix}accordion-pane {
      @include eon-typography("h5");
      font-weight: $font-weight-bold;
      padding-top: 19px;
      padding-bottom: 19px;
      padding-left: #{$accordion-inner-spacing};
      padding-right: calc(28px + $accordion-inner-spacing);

      @include for-size(desktop) {
        padding-left: #{$accordion-inner-spacing-desktop};
        padding-right: #{$accordion-inner-spacing-desktop};
      }

      @include for-size(tablet) {
        padding-left: #{$accordion-inner-spacing-tablet};
        padding-right: #{$accordion-inner-spacing-tablet};
      }

      @include for-size(phone) {
        padding-left: #{$accordion-inner-spacing-mobile};
        padding-right: #{$accordion-inner-spacing-mobile};
      }
    }

    svg,
    eon-ui-icon {
      padding-left: 4px;
      padding-right: 4px;

      @include for-size(phone) {
        padding-left: 8px;
        padding-right: 8px;
      }
    }
  }

  .#{$prefix}accordion-content {
    position: relative;
    width: 100%;
    height: auto;
  }

  .#{$prefix}view-without-icon {
    &::after {
      content: "";
      height: 0px;
    }

    &:hover {
      border: $accordion-pane-border-width solid transparent;
      background-color: transparent;

      .#{$prefix}accordion-inner {
        &::before {
          content: "" !important;
          background-color: transparent !important;
        }
      }
    }

    &.#{$prefix}active {
      border: $accordion-pane-border-width solid transparent;
      background-color: transparent;

      .#{$prefix}accordion-inner {
        &::before {
          content: "" !important;
          background-color: transparent !important;
        }
      }
    }

    .#{$prefix}accordion-pane {
      cursor: unset;

      &:focus {
        background-color: transparent;
      }
    }
  }
}
